/**
 * useDebounce - shared trailing-edge debounce (DUP-29)
 *
 * Replaces the ad-hoc pattern used by the search effects in
 * ProjectOnboardingPage:
 *   const timer = window.setTimeout(async () => { ...search... }, 250);
 *   return () => window.clearTimeout(timer);
 *
 * Semantics (matching that pattern exactly):
 * - Trailing edge only: the callback fires delayMs after the LAST call.
 *   There is no leading-edge invocation.
 * - Each new call cancels the previously scheduled one and restarts the timer.
 * - Any pending call is cancelled on unmount (like the effect cleanup did).
 * - `debounced.cancel()` cancels a pending call explicitly (the equivalent of
 *   the effect cleanup running when the guard branch returned early).
 *
 * The latest `callback` passed on each render is the one invoked, so state
 * setters and props inside it are never stale.
 */

import { useCallback, useEffect, useMemo, useRef } from 'react';

export interface DebouncedFunction<Args extends unknown[]> {
  (...args: Args): void;
  /** Cancel the pending (not yet fired) call, if any */
  cancel: () => void;
}

export function useDebounce<Args extends unknown[]>(
  callback: (...args: Args) => void,
  delayMs: number
): DebouncedFunction<Args> {
  // Always invoke the latest callback (it is usually re-created every render)
  const callbackRef = useRef(callback);
  callbackRef.current = callback;

  const timerRef = useRef<number | null>(null);

  const cancel = useCallback(() => {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const debounced = useMemo<DebouncedFunction<Args>>(() => {
    const run = ((...args: Args) => {
      cancel();
      timerRef.current = window.setTimeout(() => {
        timerRef.current = null;
        callbackRef.current(...args);
      }, delayMs);
    }) as DebouncedFunction<Args>;
    run.cancel = cancel;
    return run;
  }, [cancel, delayMs]);

  // Cancel any pending call on unmount (matches the original effect cleanup)
  useEffect(() => cancel, [cancel]);

  return debounced;
}
